import {
  Box,
  Button as ChakraButton,
  Flex,
  FormControl,
  FormLabel,
  Input,
} from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Modal, Select } from '@diamond/shared/ui';
import { useEffect, useMemo, useState } from 'react';

import type { LHCFormModalProps, LHCFormModalValues } from '../../types';
import { SyncInfo } from '../sync-info';

const EMPTY_VALUES: LHCFormModalValues = {
  lhcNo: '',
  lhcDate: '',
  collectionPlantId: '',
};

type SubmitButtonColors = {
  bgColor: string;
  color: string;
  hoverBg: string;
};

const DISABLED_COLORS: SubmitButtonColors = {
  bgColor: '#E8E8ED',
  color: '#8E8E93',
  hoverBg: '#E8E8ED',
};

const ENABLED_COLORS: SubmitButtonColors = {
  bgColor: '#0000A3',
  color: 'white',
  hoverBg: '#00008A',
};

const resolveSubmitButtonColors = (
  requireDirty: boolean,
  isDisabled: boolean
): SubmitButtonColors => {
  if (!requireDirty) {
    return ENABLED_COLORS;
  }

  return isDisabled ? DISABLED_COLORS : ENABLED_COLORS;
};

export function LHCFormModal({
  isOpen,
  onClose,
  mode,
  title,
  name,
  submitLabel,
  plantOptions,
  initialValues,
  isSubmitting,
  requireDirty = false,
  lastSyncedAt,
  sourceLhcNo,
  onSync,
  onSubmit,
}: LHCFormModalProps) {
  const [lhcNumber, setLhcNumber] = useState('');
  const [lhcDate, setLhcDate] = useState('');
  const [selectedPlant, setSelectedPlant] = useState<SelectOption | null>(null);

  const resolvedInitialValues = useMemo(
    () => initialValues ?? EMPTY_VALUES,
    [initialValues]
  );

  useEffect(() => {
    if (!isOpen) return;

    setLhcNumber(resolvedInitialValues.lhcNo);
    setLhcDate(resolvedInitialValues.lhcDate);
    setSelectedPlant(
      plantOptions.find(
        (option) => option.value === resolvedInitialValues.collectionPlantId
      ) || null
    );
  }, [isOpen, plantOptions, resolvedInitialValues]);

  const isFormValid =
    lhcNumber.trim() !== '' && lhcDate !== '' && selectedPlant !== null;

  const isDirty =
    lhcNumber.trim() !== resolvedInitialValues.lhcNo.trim() ||
    lhcDate !== resolvedInitialValues.lhcDate ||
    selectedPlant?.value !== resolvedInitialValues.collectionPlantId;

  const isDisabled =
    !isFormValid || isSubmitting || (requireDirty ? !isDirty : false);
  const submitButtonColors = resolveSubmitButtonColors(requireDirty, isDisabled);
  const modalHeaderProps =
    mode === 'duplicate'
      ? { fontSize: '24px', fontWeight: 500, pb: 2 }
      : { fontSize: { base: '16px', md: '18px' }, pb: 2 };

  const handleSubmit = () => {
    if (!selectedPlant) return;

    onSubmit({
      lhcNo: lhcNumber.trim(),
      lhcDate,
      collectionPlantId: selectedPlant.value,
    });
  };

  return (
    <Modal
      title={title}
      name={name}
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      isCentered
      hideAction
      scrollBehavior="outside"
      modalBodyProps={{ pb: 6 }}
      modalHeaderProps={modalHeaderProps}
    >
      <Flex
        align="center"
        justify="space-between"
        mb={6}
        pb={4}
        borderBottom="1px solid"
        borderColor="gray.200"
        width="100%"
      >
        <SyncInfo
          lastSyncedAt={lastSyncedAt}
          sourceLhcNo={sourceLhcNo}
          onSync={onSync}
        />
      </Flex>

      <Flex gap={4} mb={6}>
        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            {mode === 'duplicate' ? 'Nomor LHC Duplikat' : 'Nomor LHC'}
          </FormLabel>
          <Input
            aria-label="Nomor LHC"
            placeholder="Tulis nomor LHC SOL"
            value={lhcNumber}
            onChange={(event) => setLhcNumber(event.target.value)}
            size="md"
          />
        </FormControl>

        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Tanggal LHC
          </FormLabel>
          <Input
            type="date"
            placeholder="Pilih tanggal"
            value={lhcDate}
            onChange={(event) => setLhcDate(event.target.value)}
            size="md"
          />
        </FormControl>

        <FormControl flex={1}>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.600">
            Plant / Cabang
          </FormLabel>
          <Box>
            <Select
              id={`${mode}-lhc-plant`}
              placeholder="Pilih lokasi"
              options={plantOptions}
              value={selectedPlant}
              onChange={(newValue) =>
                setSelectedPlant(newValue as SelectOption | null)
              }
            />
          </Box>
        </FormControl>
      </Flex>

      <Flex justify="flex-end">
        <ChakraButton
          bgColor={submitButtonColors.bgColor}
          color={submitButtonColors.color}
          _hover={{
            bg: submitButtonColors.hoverBg,
          }}
          isDisabled={isDisabled}
          isLoading={isSubmitting}
          onClick={handleSubmit}
          px={6}
        >
          {submitLabel}
        </ChakraButton>
      </Flex>
    </Modal>
  );
}
